import { useEffect, useRef } from "react";
import aiInterviewService from "../../../Services/ai-interview.service";
import useAiAssessmentEvaluate from "./useAiAssessmentEvaluate";
import { useAIAssessmentContext } from "../../../Context/AIAssessmentContext/AIAssessmentContext";

const usePing = () => {
  const { currentSession, isRoundFinished, currentStep } =
    useAIAssessmentContext();
  const { handleUnqualifiedRound, stopAndUploadRecording } = useAiAssessmentEvaluate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastPingTimeRef = useRef<number>(0);

  const shouldPing = () => {
    return (
      currentSession &&
      currentSession.status === "pending" &&
      !isRoundFinished &&
      currentStep >= 0 &&
      navigator.onLine
    );
  };

  const performPing = async () => {
    if (!shouldPing()) {
      return;
    }

    if(!currentSession?._id) {
      return;
    }

    try {
      const pingResponse = await aiInterviewService.storePinginfo(
        currentSession?._id
      );
      lastPingTimeRef.current = Date.now();

      if (
        pingResponse &&
        pingResponse?.isDisqualified &&
        pingResponse?.jobApplication
      ) {
        await stopAndUploadRecording();
        handleUnqualifiedRound(
          pingResponse.interviewSession,
        );
      }
    } catch (error) {
      console.error("❌ Ping failed (likely network issue):", error);
    }
  };

  const startPingInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(performPing, 2000);
  };

  const stopPingInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!currentSession) return;

    startPingInterval();

    const handleOnline = () => {
      const timeSinceLastPing = Date.now() - lastPingTimeRef.current;
      if (timeSinceLastPing > 2000) {
        performPing();
      }
      startPingInterval();
    };

    const handleOffline = () => {
      stopPingInterval();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      stopPingInterval();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [currentSession, isRoundFinished, currentStep]);
};

export default usePing;
